import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faFilePdf } from "@fortawesome/pro-solid-svg-icons";
import Dropzone, {
  IFileWithMeta,
  ILayoutProps,
  IPreviewProps,
  StatusValue,
} from "react-dropzone-uploader";

import { themePalette } from "../../../theme";
import {
  LayoutContainer,
  UploadArea,
  FileThumbnailGrid,
  FileThumbnailCard,
  FileThumbnailImage,
  FileThumbnailPdfFallback,
  FileThumbnailRemoveButton,
  InputContentWrapper,
  InputContentButton,
  InputContentHintText,
  SupportedFileTypesLabel,
  DropzoneErrorText,
} from "./FileDropzoneUploader.styles";

const isImageFile = (file: File): boolean => file.type.startsWith("image/");

const formatMaxSize = (bytes: number): string => {
  const mb = bytes / 1_000_000;
  return mb >= 1 ? `${mb}MB` : `${(bytes / 1_000).toFixed(0)}KB`;
};

const ThumbnailPreview: React.FC<IPreviewProps> = ({ meta, fileWithMeta }) => {
  const showImage = isImageFile(fileWithMeta.file) && meta.previewUrl;

  return (
    <FileThumbnailCard>
      {showImage ? (
        <FileThumbnailImage src={meta.previewUrl!} alt={meta.name} />
      ) : (
        <FileThumbnailPdfFallback>
          <FontAwesomeIcon icon={faFilePdf} size="2x" />
        </FileThumbnailPdfFallback>
      )}
      <FileThumbnailRemoveButton
        type="button"
        onClick={() => fileWithMeta.remove()}
        aria-label={`Remove ${meta.name}`}
      >
        <FontAwesomeIcon icon={faXmark} size="sm" />
      </FileThumbnailRemoveButton>
    </FileThumbnailCard>
  );
};

const ThumbnailLayout: React.FC<ILayoutProps> = ({
  input,
  previews,
  dropzoneProps,
  files,
  extra: { maxFiles },
}) => (
  <LayoutContainer {...dropzoneProps}>
    {previews && previews.length > 0 && (
      <FileThumbnailGrid>{React.Children.toArray(previews)}</FileThumbnailGrid>
    )}
    {files.length < maxFiles && input}
  </LayoutContainer>
);

const DefaultInputContent = () => {
  const { t } = useTranslation();

  return (
    <InputContentWrapper>
      <InputContentButton>
        {t("upfrontPayment.fileUpload.chooseFile")}
      </InputContentButton>
      <InputContentHintText>
        {t("upfrontPayment.fileUpload.or")}
      </InputContentHintText>
      <InputContentHintText>
        {t("upfrontPayment.fileUpload.dragAndDrop")}
      </InputContentHintText>
    </InputContentWrapper>
  );
};

// react-dropzone-uploader only accepts inline CSSProperties for its internal elements
// (see node_modules/react-dropzone-uploader/dist/Dropzone.tsx lines 660-718)
const buttonStyle: React.CSSProperties = {
  position: "static",
  backgroundColor: themePalette.greenDark,
  border: "none",
  borderRadius: "24px",
  color: themePalette.white,
  fontSize: "16px",
  fontWeight: 600,
  padding: "12px 24px",
  lineHeight: "24px",
  cursor: "pointer",
};

const transparentLabelStyle: React.CSSProperties = {
  position: "static",
  background: "transparent",
  cursor: "pointer",
};

const dropzoneStyles = {
  dropzone: {
    border: "none",
    background: "transparent",
    overflow: "visible",
    minHeight: "unset",
    padding: 0,
  } as React.CSSProperties,
  inputLabel: transparentLabelStyle,
  inputLabelWithFiles: {
    ...buttonStyle,
    alignSelf: "center",
    marginTop: 0,
    marginLeft: 0,
  } as React.CSSProperties,
  preview: {
    padding: 0,
    borderBottom: "none",
  } as React.CSSProperties,
};

interface FileDropzoneUploaderProps {
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSizeBytes?: number;
  initialFiles?: File[];
  files: IFileWithMeta[];
  setFiles: React.Dispatch<React.SetStateAction<IFileWithMeta[]>>;
  error: string | null;
  setError: (error: string | null) => void;
  uploadLabel?: React.ReactNode;
  uploadMoreLabel?: React.ReactNode;
  supportedTypesLabel?: string;
}

export const FileDropzoneUploader: React.FC<FileDropzoneUploaderProps> = ({
  accept = ".png,.jpg,.jpeg,.pdf",
  multiple = true,
  maxFiles = 10,
  maxSizeBytes = 10_000_000,
  initialFiles,
  files,
  setFiles,
  error,
  setError,
  uploadLabel = <DefaultInputContent />,
  uploadMoreLabel,
  supportedTypesLabel,
}) => {
  const { t } = useTranslation();
  const normalizedUploadLabel = React.Children.toArray(uploadLabel);
  const resolvedUploadMoreLabel =
    uploadMoreLabel ?? t("upfrontPayment.fileUpload.uploadAnotherFile");
  const resolvedSupportedTypesLabel =
    supportedTypesLabel ?? t("upfrontPayment.fileUpload.supportedTypes");

  const handleChangeStatus = useCallback(
    (file: IFileWithMeta, status: StatusValue) => {
      if (status === "done") {
        setFiles((prev) => {
          if (prev.some((f) => f.meta.id === file.meta.id)) return prev;
          return [...prev, file];
        });
        setError(null);
      } else if (status === "removed") {
        setFiles((prev) => prev.filter((f) => f.meta.id !== file.meta.id));
      } else if (status === "error_file_size") {
        setError(
          t("upfrontPayment.fileUpload.errors.fileTooLarge", {
            size: formatMaxSize(maxSizeBytes),
          }),
        );
        file.remove();
      } else if (status === "rejected_file_type") {
        setError(t("upfrontPayment.fileUpload.errors.unsupportedType"));
        file.remove();
      }
    },
    [setFiles, setError, maxSizeBytes, t],
  );

  return (
    <>
      <UploadArea>
        <Dropzone
          accept={accept}
          multiple={multiple}
          maxFiles={maxFiles}
          maxSizeBytes={maxSizeBytes}
          initialFiles={initialFiles}
          onChangeStatus={handleChangeStatus}
          submitButtonDisabled
          PreviewComponent={ThumbnailPreview}
          LayoutComponent={ThumbnailLayout}
          inputContent={normalizedUploadLabel}
          inputWithFilesContent={resolvedUploadMoreLabel}
          styles={dropzoneStyles}
        />
        {files.length === 0 && resolvedSupportedTypesLabel && (
          <SupportedFileTypesLabel>
            {resolvedSupportedTypesLabel}
          </SupportedFileTypesLabel>
        )}
      </UploadArea>
      {error && <DropzoneErrorText>{error}</DropzoneErrorText>}
    </>
  );
};
